/**
 * Reports API - MBR (Monthly Business Review) API Client
 *
 * Connects to the new /mbr backend endpoints for auto-generated reports.
 */

import axios from "./client";
import { formatPercent as formatPercentBase } from "./formatPercent";

// ==================== TYPES ====================

export type SlideTemplate = "hero" | "content" | "kpi";

export type SlideVisualType =
  | "none"
  | "kpi-strip"
  | "bar-chart"
  | "line-chart"
  | "dual-metric-chart" // Two side-by-side bar charts (ADR + Occupancy)
  | "funnel" // Legacy - simple 3-stage
  | "funnel-table" // Marketing funnel table (Event, #, CVR, Bookings $)
  | "trends-line-chart" // Multi-line trends chart (Impressions, Clicks, etc.)
  | "campaign-table" // Campaign breakdown table
  | "benchmark"
  | "icon-grid"
  | "pace-curve"
  | "google-search"
  | "workflow-image" // Autumn workflow diagram image
  | "settings-table" // Season settings and override days table
  | "marketing-combined" // Consolidated marketing slide (Trends + Funnel + Campaigns / Ad Preview right side)
  | "reputation-summary" // Reputation slide — channel scores + themes + recent reviews
  | "looking-ahead-calendar"; // Looking Ahead slide — next-month calendar grid + pricing recommendations

export interface SlideBullet {
  number: number;
  title?: string;
  content: string;
}

export interface KpiItem {
  label: string;
  value: string;
  change?: string;
  changeDirection?: "up" | "down" | "neutral";
}

export interface ChartDataPoint {
  label: string;
  value: number;
  previousValue?: number;
}

/**
 * Metric value pair for current vs prior year comparison
 */
export interface MetricValuePair {
  current: number;
  priorYear: number;
}

/**
 * Available metric keys for chart dropdown
 */
export type ChartMetricKey = "revenue" | "adr" | "occupancy" | "revpar";

/**
 * Chart metric column definition for dropdown
 */
export interface ChartMetricColumn {
  key: ChartMetricKey;
  label: string;
  shortLabel: string;
  format: "currency" | "percentage" | "number";
}

/**
 * Weekly chart data with all 4 metrics available for dropdown selection
 */
export interface WeeklyChartDataPoint {
  week: string;
  metrics: {
    revenue: MetricValuePair;
    adr: MetricValuePair;
    occupancy: MetricValuePair;
    revpar: MetricValuePair;
  };
}

/**
 * Metric column definitions for the chart dropdown
 */
export const CHART_METRIC_COLUMNS: ChartMetricColumn[] = [
  { key: "revenue", label: "Revenue", shortLabel: "Revenue", format: "currency" },
  { key: "adr", label: "ADR", shortLabel: "ADR", format: "currency" },
  { key: "occupancy", label: "Occupancy", shortLabel: "Occ %", format: "percentage" },
  { key: "revpar", label: "RevPAR", shortLabel: "RevPAR", format: "currency" },
];

export interface ComparisonTableRow {
  metric: string;
  actual: number;
  priorYear: number;
  varianceDollar: number;
  /**
   * Null when the backend has no year-over-year comparison worth publishing —
   * no prior year at all, or a move so large it reads as a data artifact rather
   * than a result (server-side `believableYoyPercent`).
   *
   * Render via `formatVariancePercent`, never as a bare number: `null >= 0` is
   * true and `Math.round(null)` is 0, so treating it as a number silently
   * prints a green "+0%" — a claim that the metric was flat, which is precisely
   * what suppressing it was meant to avoid.
   */
  variancePercent: number | null;
  format: "currency" | "percentage" | "number";
}

/** Shown in place of a percentage the backend deliberately withheld. */
export const NO_COMPARISON = "—";

/** True when the backend published no comparison for this row. */
export function hasComparison(
  variancePercent: number | null | undefined,
): variancePercent is number {
  return typeof variancePercent === "number" && Number.isFinite(variancePercent);
}

/** "+20%" / "−7%" / "—". The only safe way to render `variancePercent`. */
export function formatVariancePercent(
  variancePercent: number | null | undefined,
): string {
  if (!hasComparison(variancePercent)) return NO_COMPARISON;
  return `${variancePercent >= 0 ? "+" : ""}${Math.round(variancePercent)}%`;
}

export interface AutomationStat {
  icon: string;
  value: string;
  label: string;
}

/**
 * Conquesting data for Google Search mockup visualization
 * Shows organic search result with sitelinks format
 */
export interface ConquestingData {
  hotelName: string;
  hotelDomain?: string;
  competitorName: string;
  searchImpressionShare: number;
}

/**
 * Funnel table row for marketing funnel summary
 * Matches dashboard: Event | # | CVR | Bookings $
 */
export interface FunnelTableRow {
  event: string; // "Impressions", "Clicks", "Add to Cart", "Begin Checkout", "Purchase"
  count: number;
  cvr?: number; // Conversion rate percentage (null for impressions)
  bookingsValue?: number; // Dollar value of bookings
}

/**
 * Trends line chart data point
 * For multi-line trends over time
 */
export interface TrendsDataPoint {
  date: string; // Date string (YYYY-MM-DD or display format)
  impressions: number;
  clicks: number;
  addToCart?: number;
  beginCheckout?: number;
  purchases?: number;
  searchImpressionShare?: number; // % share of brand searches Autumn appeared on (0-100)
}

/**
 * Daily traffic breakdown by source category. Mirrors the response from
 * the marketing dashboard's `/marketing/:userId/traffic-breakdown` endpoint
 * so the consolidated marketing slide's Breakdown view shows the same
 * Autumn-vs-Organic chart hoteliers see live in the dashboard.
 */
export interface TrafficBreakdownBucket {
  date: string;
  total: number;
  categories: Record<string, number>;
}
export interface TrafficBreakdownDailyData {
  buckets: TrafficBreakdownBucket[];
  granularity: string;
}

/**
 * Reputation slide data — channel scores, theme bars, and recent
 * verbatim reviews for both the report month, the prior month, and
 * lifetime. Sourced from `getMbrReviewSummary`.
 */
export interface ReputationReviewVerbatim {
  channel: string;
  rating: number;
  reviewText: string;
  reviewDate: string;
  themes: string[];
  reviewerName?: string;
}

export interface ReputationChannelScore {
  channel: string;
  averageRating: number | null;
  totalReviews: number;
}

export interface ReputationThemeRow {
  theme: string;
  count: number;
  percentage: number;
}

export interface ReputationBlock {
  totalReviews: number;
  averageRating: number | null;
  bySentiment: { positive: number; neutral: number; negative: number };
  channelScores: ReputationChannelScore[];
  topPositiveThemes: ReputationThemeRow[];
  topNegativeThemes: ReputationThemeRow[];
  recentReviews: ReputationReviewVerbatim[];
  recentNegativeReviews: ReputationReviewVerbatim[];
  recentPositiveReviews: ReputationReviewVerbatim[];
}

export interface ReputationSlideData {
  monthly: ReputationBlock;
  priorMonth: ReputationBlock;
  allTime: ReputationBlock;
  hasMonthlyReviews: boolean;
}

/**
 * Per-day calendar cell for the Looking Ahead slide. Field names mirror
 * the live Calendar tab's data shape (`adr`, `totalOccupancy`, etc.) so
 * the report-mode renderer reuses the dashboard's display logic 1:1.
 */
export interface LookaheadCalendarDay {
  date: string;
  adr: number | null;
  baseRoomTypeRate: number | null;
  actualRevpar: number | null;
  totalOccupancy: number | null;
  totalOccupancyPercentagePrevYear: number | null;
  adrPercentagePrevYear: number | null;
  pickupCount: number | null;
  pace: number | null;
  weather?: {
    icon?: string;
    conditionCategory?: string;
    temperatureMax?: number | null;
  };
  isPriceOverride?: boolean;
}

export interface LookaheadSparklinePoint {
  checkpoints: number[];
  tyOccupancy: (number | null)[];
  stlyOccupancy: number[];
}

export interface LookaheadRecommendation {
  id: string;
  stayDate: string;
  roomTypeName?: string;
  previousPrice: number | null;
  newPrice: number | null;
  priceChange: number | null;
  priceChangePercent: number | null;
  narrative: string;
  paceSituation?: string;
  type: string;
}

export interface LookaheadCalendarSlideData {
  monthLabel: string;
  monthStart: string;
  monthEnd: string;
  days: LookaheadCalendarDay[];
  sparklines?: Record<string, LookaheadSparklinePoint>;
  recommendations: LookaheadRecommendation[];
}

/**
 * Campaign table row for campaign breakdown
 * Matches dashboard: Campaign | Impressions | Clicks | CTR | Add-To-Cart | Begin-Checkout | Purchases | Bookings $ | CVR
 */
export interface CampaignTableRow {
  campaign: string;
  impressions: number;
  clicks: number;
  ctr: number; // Click-through rate percentage
  addToCart: number;
  beginCheckout: number;
  purchases: number;
  bookingsValue: number;
  cvr: number; // Conversion rate percentage
}

/**
 * Season row for settings table
 * Shows season settings applicable to the report month
 */
export interface SeasonSettingRow {
  name: string; // Season name (e.g., "Fall Season", "Peak Season")
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
  weekdayFloor: number | null;
  weekdayCeiling: number | null;
  weekendFloor: number | null;
  weekendCeiling: number | null;
}

/**
 * Override day row for settings table
 * Shows dates with manually hardcoded floor/ceiling overrides
 */
export interface OverrideDayRow {
  date: string; // YYYY-MM-DD
  dayOfWeek: string; // "Mon", "Tue", etc.
  floor: number | null;
  ceiling: number | null;
  priceOverride: number | null; // The base price override for this date
}

/**
 * Floor utilization analysis data
 * Provides nuanced insights on how floor pricing interacted with demand
 */
export interface FloorAnalysisData {
  // Summary metrics
  totalBaseRoomsSold: number;
  roomsSoldAtFloor: number;
  percentSoldAtFloor: number; // (roomsSoldAtFloor / totalBaseRoomsSold) * 100
  revenueAtFloor: number; // Total revenue from floor-priced rooms

  // Nuanced analysis breakdown
  analysis: {
    // Days where sold at floor AND high occupancy (90%+) - floor may be too low
    highOccupancyFloorDays: number;
    highOccupancyFloorRevenue: number;

    // Days where sold at floor AND low occupancy (<90%) - floor may be preventing sales
    lowOccupancyFloorDays: number;
    lowOccupancyFloorRevenue: number;

    // Average occupancy on floor-hitting days
    avgOccupancyOnFloorDays: number;
  };

  // Recommendation signal
  recommendation:
    | "raise_floor"
    | "lower_floor"
    | "maintain"
    | "insufficient_data";
  recommendationReason: string;
}

/**
 * Settings table data for the settings slide visualization
 * Contains both season settings, override days, and floor analysis
 */
export interface SettingsTableData {
  seasons: SeasonSettingRow[];
  overrides: OverrideDayRow[];
  floorAnalysis?: FloorAnalysisData; // Floor utilization analysis
  summary: {
    totalSeasons: number;
    totalOverrideDays: number;
  };
}

/**
 * Device breakdown row for audience insights
 */
export interface DeviceBreakdownRow {
  device: string;
  clicks: number;
  ctr: number;
  bookingsValue: number;
}

/**
 * Feeder market row for geographic audience data
 */
export interface FeederMarketRow {
  location: string;
  clicks: number;
  impressions: number;
  bookingsValue: number;
}

/**
 * Brand defense data for search impression share
 */
export interface BrandDefenseData {
  searchImpressionShare: number;
  otaOpportunity: number;
  hasData: boolean;
}

/**
 * Traffic share data — what % of total website traffic Autumn drives
 */
export interface TrafficShareData {
  autumnClicks: number;
  totalSessions: number;
  sharePercent: number;
  hasData: boolean;
}

/**
 * Month-over-month comparison deltas (percentage change)
 */
export interface MoMComparison {
  impressions: number;
  clicks: number;
  purchases: number;
  bookingsValue: number;
  hasData: boolean;
}

export interface MbrSlideContent {
  id: string;
  order: number;
  template: SlideTemplate;
  badge?: string;
  title: string;
  dynamicTitle?: string; // AI-generated headline conveying the main insight (e.g., "Revenue Up 36% YoY")
  subtitle?: string;
  bullets?: SlideBullet[];
  kpis?: KpiItem[]; // Legacy - being replaced by comparisonTable
  chartData?: ChartDataPoint[];
  weeklyChartData?: WeeklyChartDataPoint[];
  comparisonTable?: ComparisonTableRow[];
  automationStats?: AutomationStat[];
  conquestingData?: ConquestingData; // Google Search mockup data for competitor conquesting
  // Marketing slide data types
  funnelTableData?: FunnelTableRow[]; // Funnel summary table (Event, #, CVR, Bookings $)
  trendsChartData?: TrendsDataPoint[]; // Multi-line trends chart data
  campaignTableData?: CampaignTableRow[]; // Campaign breakdown table
  // Settings slide data
  settingsTableData?: SettingsTableData; // Season settings and override days
  // GA4-only / enhanced marketing data
  isGa4Only?: boolean; // Whether this user's PMS only supports GA4 (no ATC/BCO)
  deviceBreakdown?: DeviceBreakdownRow[]; // Device performance data
  feederMarkets?: FeederMarketRow[]; // Geographic source data
  brandDefense?: BrandDefenseData; // Brand search impression share
  momComparison?: MoMComparison; // Month-over-month comparison deltas
  trafficShare?: TrafficShareData; // What % of total website traffic Autumn drives
  trafficBreakdownData?: TrafficBreakdownDailyData; // Daily Autumn-vs-Organic breakdown for the consolidated marketing slide
  reputationData?: ReputationSlideData; // Channel scores + themes + recent reviews for the reputation slide
  lookaheadCalendarData?: LookaheadCalendarSlideData; // Next-month calendar + pricing recs for the Looking Ahead slide
  visualType: SlideVisualType;
  backgroundImage?: string;
}

export type MbrReportStatus = "generating" | "completed" | "failed";

export interface MbrReport {
  id: string;
  userId: string;
  month: number;
  year: number;
  status: MbrReportStatus;
  propertyName: string;
  slides: MbrSlideContent[];
  generatedAt?: string;
  errorMessage?: string;
  createdAt: string;
  updatedAt: string;
}

export interface AvailableMonth {
  month: number;
  year: number;
  label: string;
  reportId?: string;
  status?: MbrReportStatus;
}

// ==================== API METHODS ====================

/**
 * Get available months for reports (past 12 months)
 */
export async function getAvailableMonths(): Promise<AvailableMonth[]> {
  try {
    const response = await axios.get<{ months: AvailableMonth[] }>("/mbr/months");
    return response.data?.months || [];
  } catch (error) {
    console.error("[reports-api] getAvailableMonths failed:", error);
    return [];
  }
}

/**
 * Get a report by ID
 */
export async function getReportById(id: string): Promise<MbrReport> {
  const response = await axios.get<MbrReport>(`/mbr/reports/${id}`);
  return response.data;
}

/**
 * Get the persisted report for a specific month/year.
 *
 * Returns null when the backend has no persisted report yet (HTTP 404).
 * The endpoint is read-only and does NOT auto-generate — generation only
 * happens through `generateReport` when the user clicks "Generate".
 */
export async function getReportByMonth(
  year: number,
  month: number
): Promise<MbrReport | null> {
  try {
    const response = await axios.get<MbrReport>(
      `/mbr/reports/${year}/${month}`
    );
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Generate a report manually (for testing/admin)
 */
export async function generateReport(
  month: number,
  year: number
): Promise<MbrReport> {
  const response = await axios.post<MbrReport>(
    `/mbr/generate?month=${month}&year=${year}`
  );
  return response.data;
}

// ==================== MARKETING-ONLY REPORT API ====================
// Separate endpoints for MarketingOnly users (no pricing/revenue data)

/**
 * Get available months for marketing-only reports
 */
export async function getMarketingAvailableMonths(): Promise<AvailableMonth[]> {
  try {
    const response = await axios.get<{ months: AvailableMonth[] }>("/mbr/marketing-months");
    return response.data?.months || [];
  } catch (error) {
    console.error("[reports-api] getMarketingAvailableMonths failed:", error);
    return [];
  }
}

/**
 * Get the persisted marketing-only report for a specific month/year.
 *
 * Returns null when no report exists yet (HTTP 404). Read-only — does
 * not trigger generation.
 */
export async function getMarketingReport(
  year: number,
  month: number
): Promise<MbrReport | null> {
  try {
    const response = await axios.get<MbrReport>(
      `/mbr/marketing-reports/${year}/${month}`
    );
    return response.data;
  } catch (error: any) {
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

/**
 * Generate a marketing-only report manually (for testing/admin)
 */
export async function generateMarketingReport(
  month: number,
  year: number
): Promise<MbrReport> {
  const response = await axios.post<MbrReport>(
    `/mbr/marketing-generate?month=${month}&year=${year}`
  );
  return response.data;
}

// ==================== HELPERS ====================

export const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

export function getMonthName(month: number): string {
  return MONTH_NAMES[month - 1] || "Unknown";
}

export function formatCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function formatPercent(value: number): string {
  // Delegates to the shared util (audit UX-06). `decimals: 0` preserves the
  // prior whole-number output ("15%"); no space before "%".
  return formatPercentBase(value, { decimals: 0 });
}

export function formatChange(value: number): string {
  const sign = value >= 0 ? "+" : "";
  return `${sign}${Math.round(value)}%`;
}

// Export as namespace for convenient importing
export const reportsApi = {
  getAvailableMonths,
  getReportById,
  getReportByMonth,
  generateReport,
  // Marketing-only report functions
  getMarketingAvailableMonths,
  getMarketingReport,
  generateMarketingReport,
};

export default reportsApi;
